import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { hasText } from './utils/textUtils.js';
import { isPaywallSeo, getPaywallSeoClass } from './utils/paywallSeo.js';
import { getDirectionFromAlignmentAndTextDirection } from 'wix-rich-content-common';
import { withInteraction } from './withInteraction.js';

var viewerStyles = {"fontElementMap":"gv51F","wrapper":"qBVQi","pluginContainerMobile":"JeeAb","desktop":"jWatM","editor":"nATBK","android":"jzBsF","atomic":"_3VuCr","toolbar":"ret3w","underline":"_3gS8-","block":"_1v05b","elementSpacing":"_1Fwn6","blockStyleSpacing":"AWByc","text":"_4pWOU _1Fwn6","child":"avqoe","orderedList":"P098S","unorderedList":"-TF32","lineHeightFix":"zAHXJ","center":"J8xXO","contentCenterAlignment":"ir-bv","justify":"_8UQOT","quote":"_3Ezh- AWByc","headerOne":"_2N6hR _1Fwn6","headerTwo":"_10EHw _1Fwn6","headerThree":"XsL07 _1Fwn6","headerFour":"nCd6B _1Fwn6","headerFive":"XNdgY _1Fwn6","headerSix":"s-eKG _1Fwn6","code":"IDUQr AWByc","codeBlock":"zODqn AWByc","renderedInTable":"_2bZ7h","normalText":"gLoBF","anchor":"xejPZ","embed":"_35u42","horizontalScrollbar":"hONkS","overrideLinkColor":"gZZcf"};

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
const draftPublic = "public-DraftStyleDefault";
const draftClassNames = (listType, depth, textDirection) => [
  `${draftPublic}-${listType}ListItem`,
  `${draftPublic}-depth${depth}`,
  `${draftPublic}-list-${textDirection}`
];
const getBlockClassName = (direction, listType, depth, { isNewList }) => {
  return [
    ...draftClassNames(listType, depth, direction),
    "fixed-tab-size",
    ...isNewList ? [`${draftPublic}-reset`] : []
  ].join(" ");
};
const List = ({
  ordered,
  items,
  mergedStyles,
  textDirection,
  blockProps,
  getBlockStyleClasses,
  blockDataToStyle,
  context
}) => {
  const Component = ordered ? "ol" : "ul";
  const listType = ordered ? "ordered" : "unordered";
  const containerClassName = `${draftPublic}-${Component}`;
  const listItemTypeClassName = `${listType}List`;
  let prevDepth = 0;
  return /* @__PURE__ */ React.createElement(Component, { className: containerClassName }, items.map((children, childIndex) => {
    const dataEntry = blockProps.data.length > childIndex ? blockProps.data[childIndex] : {};
    const { interactions } = blockProps.data[childIndex];
    let paragraphGroup = [];
    const result = [];
    const alignment = (dataEntry == null ? void 0 : dataEntry.textAlignment) || context.textAlignment;
    const textClassName = getBlockStyleClasses(
      mergedStyles,
      textDirection || dataEntry.textDirection,
      alignment
    );
    const hasJustifyText = alignment === "justify" && hasText(children);
    const blockStyle = blockDataToStyle(blockProps.data[childIndex]);
    const elementProps = (key) => ({
      className: classNames(mergedStyles.elementSpacing, textClassName, {
        [viewerStyles.hasJustifyText]: hasJustifyText,
        [viewerStyles.contentCenterAlignment]: alignment === "center"
      }),
      style: { lineHeight: blockStyle.lineHeight },
      key
    });
    React.Children.forEach(children, (child, i) => {
      if (child) {
        if (typeof child.type === "string" && /h\d/.exec(child.type)) {
          if (paragraphGroup.length) {
            result.push(/* @__PURE__ */ React.createElement("p", __spreadValues({}, elementProps(i)), paragraphGroup));
            paragraphGroup = [];
          }
          result.push(React.cloneElement(child, elementProps(i)));
        } else {
          paragraphGroup.push(child);
        }
      }
    });
    if (paragraphGroup.length) {
      result.push(/* @__PURE__ */ React.createElement("p", __spreadValues({}, elementProps("just_some_key")), paragraphGroup));
    }
    const depth = dataEntry.depth;
    const listItemDirection = getDirectionFromAlignmentAndTextDirection(
      alignment,
      textDirection || dataEntry.textDirection
    );
    const className = getBlockClassName(listItemDirection, listType, depth, {
      isNewList: childIndex === 0 || depth > prevDepth
    });
    prevDepth = depth;
    const blockIndex = dataEntry.index;
    const wrappedBlock = withInteraction(
      result.length === 0 ? " " : result,
      interactions,
      context
    );
    return /* @__PURE__ */ React.createElement(
      "li",
      {
        id: `viewer-${blockProps.keys[childIndex]}`,
        className: classNames(
          context.theme[listItemTypeClassName],
          viewerStyles[listItemTypeClassName],
          viewerStyles[alignment],
          getBlockStyleClasses(mergedStyles, listItemDirection, alignment, className, true),
          isPaywallSeo(context.seoMode) && getPaywallSeoClass(context.seoMode.paywall, blockIndex),
          blockStyle.lineHeight && viewerStyles.lineHeightFix
        ),
        key: blockProps.keys[childIndex],
        style: blockDataToStyle(blockProps.data[childIndex])
      },
      wrappedBlock
    );
  }));
};
List.propTypes = {
  blockDataToStyle: PropTypes.func,
  blockProps: PropTypes.object,
  getBlockStyleClasses: PropTypes.func,
  items: PropTypes.array,
  mergedStyles: PropTypes.object,
  ordered: PropTypes.bool,
  textDirection: PropTypes.oneOf(["rtl", "ltr"]),
  context: PropTypes.shape({
    theme: PropTypes.object.isRequired,
    experiments: PropTypes.object,
    anchorTarget: PropTypes.string.isRequired,
    relValue: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired,
    isMobile: PropTypes.bool.isRequired,
    helpers: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    seoMode: PropTypes.bool,
    contentState: PropTypes.object,
    disableRightClick: PropTypes.bool,
    textAlignment: PropTypes.oneOf(["left", "right"])
  }).isRequired
};

export { List as L, viewerStyles as v };
//# sourceMappingURL=List-739d3793.js.map
